// DataPilot / presentation demo dataset.
//
// When VITE_DEMO=1, api/client.ts swaps the axios adapter for one that resolves
// every reporting request from this file — no backend, ClickHouse, Redpanda or
// bs-iam required. Numbers are generated deterministically (a hash of
// date+site+currency seeds the value) so the same day always renders the same
// figures, and a couple of deliberate anomalies are injected so DataPilot has a
// story to tell during a client demo.

// ---- deterministic PRNG ----------------------------------------------------

// Stable hash → [0,1). No Math.random, so the dashboard is reproducible.
function seeded(key: string): number {
  let h = 2166136261
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  // xorshift mix
  h ^= h << 13
  h ^= h >>> 17
  h ^= h << 5
  return ((h >>> 0) % 100000) / 100000
}

// ---- demo dimensions -------------------------------------------------------

export interface DemoSite {
  id: number
  name: string
}

export interface DemoTenant {
  id: number
  name: string
  sites: { bookmaker_id: number; name: string }[]
}

export const DEMO_SITES: DemoSite[] = [
  { id: 1, name: 'BetBoom' },
  { id: 2, name: 'GalaxyBet' },
  { id: 3, name: 'AurumPlay' },
]

export const DEMO_CURRENCIES = ['EUR', 'BRL']

export const DEMO_TENANTS: DemoTenant[] = [
  {
    id: 101,
    name: 'BetStarters Demo Group',
    sites: DEMO_SITES.map((s) => ({ bookmaker_id: s.id, name: s.name })),
  },
]

export const DEMO_USER = {
  id: 1,
  email: 'demo@betstarters.com',
  first_name: 'Demo',
  last_name: 'Analyst',
  platform_role: 'PLATFORM_ADMIN',
  pref_language: 'en',
  tenants: DEMO_TENANTS.map((t) => ({ id: t.id, name: t.name })),
}

// Per-site scale so brands look distinct (BetBoom is the big one).
const SITE_SCALE: Record<number, number> = { 1: 1.0, 2: 0.55, 3: 0.3 }
// BRL figures are ~5.5x EUR in nominal terms.
const CCY_SCALE: Record<string, number> = { EUR: 1.0, BRL: 5.5 }

// ---- date helpers ----------------------------------------------------------

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function eachDay(from: string, to: string): string[] {
  const out: string[] = []
  const start = new Date(from + 'T00:00:00Z')
  const end = new Date(to + 'T00:00:00Z')
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(isoDay(d))
  }
  return out
}

// daysAgo: how many days before "today" a date is (for injecting anomalies on
// a recent, always-present day regardless of the selected window).
function daysAgo(dateStr: string): number {
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)
  const d = new Date(dateStr + 'T00:00:00Z')
  return Math.round((today.getTime() - d.getTime()) / 86400000)
}

// ---- core daily figure generator ------------------------------------------

interface DailyFigures {
  turnover: number
  ggrReal: number
  ggrBonus: number
  ggr: number
  ngr: number
  deposits: number
  withdrawals: number
  ftdCount: number
  ftdAmount: number
  activePlayers: number
  newPlayers: number
  bonusCost: number
}

// Weekly seasonality: weekends run hotter.
function weekendBoost(dateStr: string): number {
  const dow = new Date(dateStr + 'T00:00:00Z').getUTCDay()
  return dow === 0 || dow === 6 ? 1.25 : 1.0
}

function dailyFigures(dateStr: string, siteId: number, currency: string): DailyFigures {
  const r = seeded(`${dateStr}|${siteId}|${currency}`)
  const scale = (SITE_SCALE[siteId] ?? 0.4) * (CCY_SCALE[currency] ?? 1)
  const season = weekendBoost(dateStr)
  const noise = 0.85 + r * 0.3 // ±15%

  const turnover = Math.round(420000 * scale * season * noise)
  const margin = 0.045 + seeded(`m|${dateStr}|${siteId}`) * 0.02 // 4.5–6.5%
  let ggrReal = Math.round(turnover * margin)
  const ggrBonus = Math.round(ggrReal * 0.12)
  const ggr = ggrReal + ggrBonus

  let bonusCost = Math.round(ggrReal * 0.18)
  const fees = Math.round(ggrReal * 0.14)
  let ngr = ggrReal - bonusCost - fees

  const deposits = Math.round(turnover * 0.16 * (0.9 + seeded(`d|${dateStr}|${siteId}`) * 0.2))
  let withdrawals = Math.round(deposits * (0.55 + seeded(`w|${dateStr}|${siteId}`) * 0.2))

  let ftdCount = Math.round(70 * SITE_SCALE[siteId]! * season * (0.7 + r * 0.6))
  const ftdAmount = Math.round(ftdCount * (45 + seeded(`f|${dateStr}|${siteId}`) * 30) * (CCY_SCALE[currency] ?? 1))

  const activePlayers = Math.round(3200 * SITE_SCALE[siteId]! * season * (0.85 + r * 0.3))
  const newPlayers = Math.round(ftdCount * (1.6 + seeded(`n|${dateStr}|${siteId}`) * 0.8))

  // ---- injected demo anomalies (only on EUR base so the story is clear) ----
  const ago = daysAgo(dateStr)
  // 1) Acquisition campaign on BetBoom 5 days ago: FTD spike.
  if (siteId === 1 && ago === 5) {
    ftdCount = Math.round(ftdCount * 2.4)
  }
  // 2) Withdrawal surge / low hold on AurumPlay 3 days ago.
  if (siteId === 3 && ago === 3) {
    withdrawals = Math.round(deposits * 1.15)
  }
  // 3) Bonus over-spend on GalaxyBet 4 days ago: NGR squeezed.
  if (siteId === 2 && ago === 4) {
    bonusCost = Math.round(ggrReal * 0.42)
    ngr = ggrReal - bonusCost - fees
  }

  return {
    turnover,
    ggrReal,
    ggrBonus,
    ggr,
    ngr,
    deposits,
    withdrawals,
    ftdCount,
    ftdAmount,
    activePlayers,
    newPlayers,
    bonusCost,
  }
}

// ---- param parsing ---------------------------------------------------------

interface ReportParams {
  date_from?: string
  date_to?: string
  site_ids?: string
  display_currency?: string
  cohort_month?: string
  [k: string]: unknown
}

function selectedSites(p: ReportParams): number[] {
  if (p.site_ids && String(p.site_ids).trim() !== '') {
    return String(p.site_ids)
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n))
  }
  return DEMO_SITES.map((s) => s.id)
}

// Which currencies to emit. BASE/native → all; a specific code → just that one.
function selectedCurrencies(p: ReportParams): string[] {
  const dc = String(p.display_currency ?? '').toUpperCase()
  if (dc && dc !== 'BASE' && dc !== 'NATIVE' && DEMO_CURRENCIES.includes(dc)) {
    return [dc]
  }
  if (dc === 'EUR' || dc === 'USD' || dc === '') return ['EUR']
  return DEMO_CURRENCIES
}

function range(p: ReportParams): string[] {
  const to = p.date_to ?? isoDay(new Date())
  const from = p.date_from ?? to
  return eachDay(from, to)
}

// ---- per-endpoint row builders --------------------------------------------

function ggrRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      for (const ccy of selectedCurrencies(p)) {
        const f = dailyFigures(date, site, ccy)
        rows.push({
          date,
          site_id: site,
          currency: ccy,
          ggr_real: f.ggrReal,
          ggr_bonus: f.ggrBonus,
          ggr: f.ggr,
          ngr: f.ngr,
          turnover: f.turnover,
          unique_player_count: f.activePlayers,
        })
      }
    }
  }
  return rows
}

const PAYMENT_METHODS = ['PIX', 'Card', 'Bank Transfer', 'E-Wallet']

function depositRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      for (const ccy of selectedCurrencies(p)) {
        const f = dailyFigures(date, site, ccy)
        rows.push({
          date,
          site_id: site,
          currency: ccy,
          total_deposits: f.deposits,
          amount: f.deposits,
          count: Math.round(f.deposits / 90),
          deposit_count: Math.round(f.deposits / 90),
          unique_depositors: Math.round(f.activePlayers * 0.4),
          payment_method: PAYMENT_METHODS[site % PAYMENT_METHODS.length],
          payment_method_type: site % 2 === 0 ? 'card' : 'instant',
        })
      }
    }
  }
  return rows
}

function withdrawalRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      for (const ccy of selectedCurrencies(p)) {
        const f = dailyFigures(date, site, ccy)
        rows.push({
          date,
          site_id: site,
          currency: ccy,
          total_withdrawals: f.withdrawals,
          amount: f.withdrawals,
          count: Math.round(f.withdrawals / 140),
          withdrawal_count: Math.round(f.withdrawals / 140),
        })
      }
    }
  }
  return rows
}

// Cashier overview returns the nested { rows, totals } shape the Financial
// Overview panel expects: rows = per bucket+site, totals = period aggregate
// (one entry per currency).
function cashierOverview(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  const agg: Record<string, { currency: string; total_deposits: number; total_withdrawals: number; deposit_count: number; withdrawal_count: number; unique_depositors: number }> = {}
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      for (const ccy of selectedCurrencies(p)) {
        const f = dailyFigures(date, site, ccy)
        const net = f.deposits - f.withdrawals
        const depCount = Math.round(f.deposits / 90)
        const wdCount = Math.round(f.withdrawals / 140)
        const depositors = Math.round(f.activePlayers * 0.4)
        rows.push({
          date,
          site_id: site,
          currency: ccy,
          total_deposits: f.deposits,
          total_withdrawals: f.withdrawals,
          net_cash: net,
          net_cash_pct: f.deposits ? net / f.deposits : 0,
          deposit_count: depCount,
          withdrawal_count: wdCount,
          unique_depositors: depositors,
        })
        const a = (agg[ccy] ??= { currency: ccy, total_deposits: 0, total_withdrawals: 0, deposit_count: 0, withdrawal_count: 0, unique_depositors: 0 })
        a.total_deposits += f.deposits
        a.total_withdrawals += f.withdrawals
        a.deposit_count += depCount
        a.withdrawal_count += wdCount
        a.unique_depositors += depositors
      }
    }
  }
  const totals = Object.values(agg).map((a) => {
    // Period-unique depositors would be deduped server-side; dampen the daily
    // sum so the demo figure looks like a realistic period unique.
    const uniq = Math.round(a.unique_depositors * 0.18)
    const net = a.total_deposits - a.total_withdrawals
    return {
      currency: a.currency,
      total_deposits: a.total_deposits,
      total_withdrawals: a.total_withdrawals,
      net_cash: net,
      net_cash_pct: a.total_deposits ? net / a.total_deposits : 0,
      deposit_count: a.deposit_count,
      withdrawal_count: a.withdrawal_count,
      unique_depositors: uniq,
    }
  })
  return { rows, totals }
}

const PM_TYPE: Record<string, string> = { PIX: 'instant', Card: 'card', 'Bank Transfer': 'bank', 'E-Wallet': 'ewallet' }

function mixRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  const ccy = selectedCurrencies(p)[0]
  let totalAll = 0
  const byMethod: Record<string, number> = {}
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      const f = dailyFigures(date, site, ccy)
      // Spread each day's deposits across the methods deterministically.
      PAYMENT_METHODS.forEach((pm, i) => {
        const share = [0.45, 0.3, 0.15, 0.1][i] ?? 0.1
        byMethod[pm] = (byMethod[pm] ?? 0) + f.deposits * share
        totalAll += f.deposits * share
      })
    }
  }
  for (const [pm, amount] of Object.entries(byMethod)) {
    const amt = Math.round(amount)
    rows.push({
      payment_method: pm,
      payment_method_type: PM_TYPE[pm] ?? 'instant',
      currency: ccy,
      site_id: 0,
      total_amount: amt,
      amount: amt,
      transaction_count: Math.round(amt / 90),
      count: Math.round(amt / 90),
      unique_players: Math.round(amt / 250),
      pct_of_total: totalAll ? (amt / totalAll) * 100 : 0,
    })
  }
  return rows
}

function ftdRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      for (const ccy of selectedCurrencies(p)) {
        const f = dailyFigures(date, site, ccy)
        rows.push({
          date,
          site_id: site,
          currency: ccy,
          payment_method_type: site % 2 === 0 ? 'card' : 'instant',
          ftd_count: f.ftdCount,
          ftd_amount: f.ftdAmount,
          avg_ftd_value: f.ftdCount ? Math.round(f.ftdAmount / f.ftdCount) : 0,
        })
      }
    }
  }
  return rows
}

function bonusRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      const f = dailyFigures(date, site, 'EUR')
      const emitted = Math.round(f.deposits * 0.12)
      let rate = 0.55 + seeded(`br|${date}|${site}`) * 0.25 // 55–80% redemption
      // Demo anomaly: GalaxyBet ran an inefficient promo 4 days ago (low redemption).
      if (site === 2 && daysAgo(date) === 4) rate = 0.24
      const redeemed = Math.round(emitted * rate)
      rows.push({
        date,
        site_id: site,
        bonus_emitted: emitted,
        bonus_redeemed: redeemed,
        emitted_count: Math.round(emitted / 35),
        redeemed_count: Math.round(redeemed / 40),
        unique_players: Math.round(emitted / 55),
      })
    }
  }
  return rows
}

// ---- sportsbook ------------------------------------------------------------

const DEMO_SPORTS = [
  { id: 1, name: 'Football', share: 0.42 },
  { id: 2, name: 'Basketball', share: 0.18 },
  { id: 3, name: 'Tennis', share: 0.14 },
  { id: 4, name: 'eSports', share: 0.12 },
  { id: 5, name: 'Volleyball', share: 0.08 },
  { id: 6, name: 'Ice Hockey', share: 0.06 },
]

// Per-day sportsbook figures for one site (sport ≈ 35% of total turnover).
function sportFigures(date: string, site: number) {
  const f = dailyFigures(date, site, 'EUR')
  const turnover = Math.round(f.turnover * 0.35)
  let margin = 0.06 + seeded(`sm|${date}|${site}`) * 0.04 // 6–10%
  // Demo anomaly: a big upset on BetBoom 6 days ago crushes the margin.
  if (site === 1 && daysAgo(date) === 6) margin = 0.015
  const ggr = Math.round(turnover * margin)
  const cancelled = Math.round(turnover * 0.03)
  const win = turnover - ggr - cancelled
  return {
    turnover,
    win,
    cancelled,
    ggr,
    bet_count: Math.round(turnover / 30),
    unique_players: Math.round(f.activePlayers * 0.45),
  }
}

function sportsbookRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  const byDate: Record<string, { turnover: number; win: number; cancelled: number; ggr: number; bet_count: number; unique_players: number }> = {}
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      const s = sportFigures(date, site)
      const a = (byDate[date] ??= { turnover: 0, win: 0, cancelled: 0, ggr: 0, bet_count: 0, unique_players: 0 })
      a.turnover += s.turnover; a.win += s.win; a.cancelled += s.cancelled
      a.ggr += s.ggr; a.bet_count += s.bet_count; a.unique_players += s.unique_players
    }
  }
  for (const date of Object.keys(byDate).sort()) {
    const a = byDate[date]
    rows.push({ date, site_id: 0, ...a })
  }
  return rows
}

function sportsbookBySport(p: ReportParams) {
  // Period totals, then split across sports by share with slightly varied margin.
  let turnover = 0
  for (const date of range(p)) for (const site of selectedSites(p)) turnover += sportFigures(date, site).turnover
  return DEMO_SPORTS.map((s) => {
    const t = Math.round(turnover * s.share)
    const margin = 0.06 + seeded(`spm|${s.id}`) * 0.04
    return {
      sport: s.name,
      sport_id: s.id,
      turnover: t,
      ggr: Math.round(t * margin),
      bet_count: Math.round(t / 30),
      unique_players: Math.round(t / 220),
    }
  })
}

function activePlayerRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  for (const date of range(p)) {
    for (const site of selectedSites(p)) {
      const f = dailyFigures(date, site, 'EUR')
      rows.push({
        date,
        site_id: site,
        active_players: f.activePlayers,
        new_players: f.newPlayers,
      })
    }
  }
  return rows
}

function cohortRows(p: ReportParams) {
  // Build 12 monthly cohorts with decaying retention.
  const month = p.cohort_month ?? isoDay(new Date()).slice(0, 7)
  const channels = ['Affiliates', 'Paid Search', 'Organic', 'Social']
  const rows: Record<string, unknown>[] = []
  for (let i = 0; i < 12; i++) {
    const base = seeded(`cohort|${month}|${i}`)
    const d1 = Math.round((55 + base * 20) * 10) / 10
    const d7 = Math.round((34 + base * 15) * 10) / 10
    const d30 = Math.round((19 + base * 12) * 10) / 10
    const d90 = Math.round((11 + base * 8) * 10) / 10
    rows.push({
      registration_date: `${month}-${String(i + 1).padStart(2, '0')}`,
      cohort_date: `${month}-${String(i + 1).padStart(2, '0')}`,
      acquisition_channel: channels[i % channels.length],
      d1_retention: d1, d7_retention: d7, d30_retention: d30, d90_retention: d90,
      d1_pct: d1, d7_pct: d7, d30_pct: d30, d90_pct: d90,
    })
  }
  return rows
}

// ---- games -----------------------------------------------------------------

const DEMO_PLAYERS = [
  { id: '10482' }, { id: '20917' }, { id: '33145' }, { id: '41882' },
  { id: '55230' }, { id: '61777' }, { id: '72604' }, { id: '88391' },
]

const DEMO_GAMES = [
  { game_id: 9001, game_name: 'Aurora Riches', brand_id: 11, brand_name: 'Pragmatic', game_type: 'slot' },
  { game_id: 9002, game_name: 'Live Roulette VIP', brand_id: 12, brand_name: 'Evolution', game_type: 'live' },
  { game_id: 9003, game_name: 'Crash Rocket', brand_id: 13, brand_name: 'Spribe', game_type: 'crash' },
  { game_id: 9004, game_name: 'Gold Blackjack', brand_id: 12, brand_name: 'Evolution', game_type: 'table' },
  { game_id: 9005, game_name: 'Mega Fortune Wheel', brand_id: 11, brand_name: 'Pragmatic', game_type: 'slot' },
]

// effective_rtp / avg_rtp are FRACTIONS (the UI multiplies by 100);
// margin / pct_of_total are already percentage numbers.
function gamePerformanceRows(p: ReportParams) {
  const ccy = selectedCurrencies(p)[0]
  const rows: Record<string, unknown>[] = []
  for (const g of DEMO_GAMES) {
    const r = seeded(`game|${g.game_id}`)
    const turnover = Math.round(180000 * (0.4 + r) * (CCY_SCALE[ccy] ?? 1))
    const rtp = 0.94 + r * 0.04 // fraction 0.94–0.98
    const totalWin = Math.round(turnover * rtp)
    const ggr = turnover - totalWin
    rows.push({
      ...g,
      site_id: 1,
      date: isoDay(new Date()),
      currency: ccy,
      total_bet: turnover,
      total_win: totalWin,
      turnover,
      total_turnover: turnover,
      ggr,
      total_ggr: ggr,
      round_count: Math.round(turnover / 12),
      total_rounds: Math.round(turnover / 12),
      unique_players: Math.round(900 * (0.4 + r)),
      effective_rtp: Math.round(rtp * 10000) / 10000,
    })
  }
  return rows
}

// Nested { rows (per-day trend), totals (period aggregate + prev_* for deltas) }.
function gameOverview(p: ReportParams) {
  const perf = gamePerformanceRows(p)
  const totGgr = perf.reduce((a, r) => a + (r.ggr as number), 0)
  const totTo = perf.reduce((a, r) => a + (r.turnover as number), 0)
  const totRounds = perf.reduce((a, r) => a + (r.round_count as number), 0)
  const totPlayers = perf.reduce((a, r) => a + (r.unique_players as number), 0)
  const avgRtp = totTo ? (totTo - totGgr) / totTo : 0.956

  // Per-day trend spread across the selected window.
  const dates = range(p)
  const rows = dates.map((date) => {
    const f = 0.85 + seeded(`gtrend|${date}`) * 0.3
    return {
      date,
      ggr: Math.round((totGgr / dates.length) * f),
      turnover: Math.round((totTo / dates.length) * f),
      round_count: Math.round((totRounds / dates.length) * f),
      unique_players: Math.round((totPlayers / dates.length) * f),
      unique_games: DEMO_GAMES.length,
    }
  })

  const totals = [{
    currency: selectedCurrencies(p)[0],
    total_ggr: totGgr,
    total_turnover: totTo,
    total_rounds: totRounds,
    unique_players: Math.round(totPlayers * 0.4),
    unique_games: DEMO_GAMES.length,
    avg_rtp: avgRtp,
    // previous period ~5-8% lower, for the delta badges.
    prev_ggr: Math.round(totGgr * 0.93),
    prev_turnover: Math.round(totTo * 0.95),
    prev_rounds: Math.round(totRounds * 0.96),
    prev_players: Math.round(totPlayers * 0.4 * 0.92),
    prev_games: DEMO_GAMES.length,
    prev_rtp: avgRtp * 1.002,
  }]
  return { rows, totals }
}

function gameMixRows(p: ReportParams) {
  const perf = gamePerformanceRows(p)
  const byType: Record<string, number> = {}
  let total = 0
  for (const r of perf) {
    const t = r.game_type as string
    byType[t] = (byType[t] ?? 0) + (r.turnover as number)
    total += r.turnover as number
  }
  return Object.entries(byType).map(([game_type, turnover]) => ({
    game_type,
    currency: selectedCurrencies(p)[0],
    turnover,
    pct_of_total: total ? (turnover / total) * 100 : 0,
  }))
}

function brandMarginRows(p: ReportParams) {
  const perf = gamePerformanceRows(p)
  const byBrand: Record<string, { brand_id: number; ggr: number; turnover: number; win: number; rounds: number; players: number }> = {}
  for (const r of perf) {
    const k = r.brand_name as string
    const cur = byBrand[k] ?? { brand_id: r.brand_id as number, ggr: 0, turnover: 0, win: 0, rounds: 0, players: 0 }
    cur.ggr += r.ggr as number
    cur.turnover += r.turnover as number
    cur.win += r.total_win as number
    cur.rounds += r.round_count as number
    cur.players += r.unique_players as number
    byBrand[k] = cur
  }
  return Object.entries(byBrand).map(([brand_name, v]) => ({
    brand_name,
    brand_id: v.brand_id,
    site_id: 1,
    date: isoDay(new Date()),
    currency: selectedCurrencies(p)[0],
    ggr: v.ggr,
    turnover: v.turnover,
    round_count: v.rounds,
    unique_players: v.players,
    margin: v.turnover ? (v.ggr / v.turnover) * 100 : 0, // percentage number
    margin_pct: v.turnover ? (v.ggr / v.turnover) * 100 : 0,
    effective_rtp: v.turnover ? v.win / v.turnover : 0, // fraction
  }))
}

// Per-player breakdown for the game drill-down modal.
function gamePlayersRows(p: ReportParams) {
  const ccy = selectedCurrencies(p)[0]
  return DEMO_PLAYERS.slice(0, 6).map((pl, i) => {
    const r = seeded(`gplayer|${pl.id}|${p.game_id ?? 0}`)
    const bet = Math.round(12000 * (0.4 + r) * (CCY_SCALE[ccy] ?? 1) * (1 - i * 0.1))
    const win = Math.round(bet * (0.93 + r * 0.05))
    return {
      player_id: pl.id,
      site_id: 1,
      currency: ccy,
      total_bet: bet,
      total_win: win,
      ggr: bet - win,
      round_count: Math.round(bet / 10),
    }
  })
}

// ---- players (leaderboard + detail) ----------------------------------------

const PLAYER_NAMES = [
  'marco_v', 'lucas88', 'sofia_r', 'diego_pro', 'elena_x', 'tiago_bet', 'carla_w', 'bruno_ace',
  'ana_lux', 'pedro77', 'giulia_z', 'mateo_g', 'laura_k', 'hugo_win', 'nadia_q', 'ivan_slot',
  'paula_b', 'yuki_999', 'omar_t', 'lena_star', 'rafa_gg', 'bea_lucky', 'nico_high', 'vera_rio',
  'theo_max', 'mara_ng', 'leo_bonus', 'ines_p', 'dani_roll', 'sara_vip', 'felix_o', 'cora_d',
  'adam_pix', 'noa_spin', 'eric_jack', 'mia_gold',
]
const PLAYER_COUNTRIES = ['BR', 'MX', 'CL', 'PE', 'CO', 'PT']

interface DemoPlayer {
  player_id: string
  username: string
  site_id: number
  country: string
  currency: string
}

const DEMO_PLAYER_ROSTER: DemoPlayer[] = PLAYER_NAMES.map((username, i) => ({
  player_id: String(100001 + i),
  username,
  site_id: DEMO_SITES[i % DEMO_SITES.length].id,
  country: PLAYER_COUNTRIES[i % PLAYER_COUNTRIES.length],
  currency: i % DEMO_SITES.length === 0 ? 'BRL' : 'EUR',
}))

const rosterById: Record<string, DemoPlayer> = Object.fromEntries(
  DEMO_PLAYER_ROSTER.map((p) => [p.player_id, p]),
)

// Deterministic per-player economics. Net cash spans strongly positive
// (operator-favourable "top performers") to negative (big winners — the
// "bottom performers" worth a closer look).
interface PlayerFinance {
  deposits: number
  withdrawals: number
  netCash: number
  depositCount: number
  withdrawalCount: number
  ftdAmount: number
  casinoGGR: number
  sportGGR: number
  casinoBet: number
  sportBet: number
  rounds: number
  bets: number
}

function playerFinance(id: string): PlayerFinance {
  const r = seeded(`pf|${id}`)
  const deposits = Math.round(2000 + r * 48000)
  const wdRatio = 0.3 + seeded(`pw|${id}`) * 1.1 // 0.3–1.4 → some net-negative
  const withdrawals = Math.round(deposits * wdRatio)
  const casinoBet = Math.round(deposits * (3 + seeded(`pcb|${id}`) * 6))
  const sportBet = Math.round(deposits * (1 + seeded(`psb|${id}`) * 3))
  const casinoGGR = Math.round(casinoBet * (0.03 + seeded(`pcg|${id}`) * 0.04))
  const sportGGR = Math.round(sportBet * (0.04 + seeded(`psg|${id}`) * 0.05))
  return {
    deposits,
    withdrawals,
    netCash: deposits - withdrawals,
    depositCount: Math.round(5 + r * 60),
    withdrawalCount: Math.round(2 + seeded(`pwc|${id}`) * 20),
    ftdAmount: Math.round(20 + seeded(`pftd|${id}`) * 180),
    casinoGGR,
    sportGGR,
    casinoBet,
    sportBet,
    rounds: Math.round(casinoBet / 8),
    bets: Math.round(sportBet / 25),
  }
}

function recentDate(daysBack: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - daysBack)
  return isoDay(d)
}

// GET /reports/cashier/players — the leaderboard source.
function playerCashierRows(p: ReportParams) {
  const sites = selectedSites(p)
  const search = String(p.player_search ?? '').toLowerCase()
  const ftdOnly = p.ftd_only === true || p.ftd_only === 'true'
  let roster = DEMO_PLAYER_ROSTER.filter((pl) => sites.includes(pl.site_id))
  if (search) roster = roster.filter((pl) => pl.username.includes(search) || pl.player_id.includes(search))
  const rows = roster.map((pl) => {
    const f = playerFinance(pl.player_id)
    return {
      player_id: pl.player_id,
      username: pl.username, // extra (demo) so the leaderboard can show names
      site_id: pl.site_id,
      total_deposits: f.deposits,
      total_withdrawals: f.withdrawals,
      net_cash: f.netCash,
      deposit_count: f.depositCount,
      withdrawal_count: f.withdrawalCount,
      ftd_amount: f.ftdAmount,
      ggr: f.casinoGGR + f.sportGGR, // extra (demo): total gaming revenue
      first_deposit_at: recentDate(60 + (Number(pl.player_id) % 120)),
      last_activity_at: recentDate(Number(pl.player_id) % 14),
    }
  })
  if (ftdOnly) return rows // (all demo players have an FTD)
  const limit = typeof p.limit === 'number' ? p.limit : 1000
  return rows.slice(0, limit)
}

// GET /reports/players/leaderboard — GGR ranking (casino + sport) per player.
function playerLeaderboardRows(p: ReportParams) {
  const sites = selectedSites(p)
  const roster = DEMO_PLAYER_ROSTER.filter((pl) => sites.includes(pl.site_id))
  const rows = roster.map((pl) => {
    const f = playerFinance(pl.player_id)
    return {
      player_id: pl.player_id,
      username: pl.username,
      site_id: pl.site_id,
      casino_ggr: f.casinoGGR,
      sport_ggr: f.sportGGR,
      ggr: f.casinoGGR + f.sportGGR,
      total_deposits: f.deposits,
      total_withdrawals: f.withdrawals,
      net_cash: f.netCash,
    }
  })
  rows.sort((a, b) => b.ggr - a.ggr || a.player_id.localeCompare(b.player_id))
  const limit = typeof p.limit === 'number' ? p.limit : 500
  return rows.slice(0, limit)
}

// GET /reports/players/detail/profile
function playerProfile(p: ReportParams) {
  const id = String(p.player_id ?? '')
  const pl = rosterById[id]
  if (!pl) return null
  const f = playerFinance(id)
  return {
    player_id: id,
    site_id: pl.site_id,
    username: pl.username,
    country_code: pl.country,
    language_code: pl.country === 'PT' ? 'pt' : 'es',
    currency_code: pl.currency,
    registration_date: recentDate(120 + (Number(id) % 240)),
    status: Number(id) % 11 === 0 ? 'suspended' : 'active',
    kyc_level: Number(id) % 3 === 0 ? 'verified' : 'pending',
    affiliate_id: `AFF-${100 + (Number(id) % 20)}`,
    first_deposit_date: recentDate(60 + (Number(id) % 120)),
    first_deposit_amount: f.ftdAmount,
    last_login_at: recentDate(Number(id) % 14),
    total_logins: Math.round(40 + seeded(`plog|${id}`) * 400),
  }
}

// GET /reports/players/detail/summary
function playerSummary(p: ReportParams) {
  const id = String(p.player_id ?? '')
  if (!rosterById[id]) return null
  const f = playerFinance(id)
  return {
    casino_bet_amount: f.casinoBet,
    casino_win_amount: f.casinoBet - f.casinoGGR,
    casino_ggr: f.casinoGGR,
    casino_rounds: f.rounds,
    casino_sessions: Math.round(f.rounds / 30),
    unique_games: 8 + (Number(id) % 14),
    sport_bet_amount: f.sportBet,
    sport_win_amount: f.sportBet - f.sportGGR,
    sport_ggr: f.sportGGR,
    sport_bets: f.bets,
    total_deposits: f.deposits,
    total_withdrawals: f.withdrawals,
    net_cash: f.netCash,
    deposit_count: f.depositCount,
    withdrawal_count: f.withdrawalCount,
    bonus_awarded: Math.round(f.deposits * 0.12),
    bonus_redeemed: Math.round(f.deposits * 0.08),
  }
}

const PLAYER_GAMES = ['Aurora Riches', 'Live Roulette VIP', 'Crash Rocket', 'Gold Blackjack', 'Mega Fortune Wheel', 'Sweet Bonanza']
const PLAYER_SPORTS = ['Football', 'Tennis', 'Basketball', 'eSports']

function playerCasinoGames(p: ReportParams) {
  const id = String(p.player_id ?? '')
  if (!rosterById[id]) return []
  const f = playerFinance(id)
  return PLAYER_GAMES.map((game_name, i) => {
    const share = [0.34, 0.24, 0.16, 0.12, 0.08, 0.06][i] ?? 0.05
    const bet = Math.round(f.casinoBet * share)
    const ggr = Math.round(f.casinoGGR * share)
    return {
      game_id: 9001 + i,
      game_name,
      game_type: i === 1 ? 'live' : i === 2 ? 'crash' : i === 3 ? 'table' : 'slot',
      bet_amount: bet,
      win_amount: bet - ggr,
      ggr,
      rounds: Math.round(bet / 8),
      rtp: bet ? (bet - ggr) / bet : 0.95, // fraction
    }
  })
}

function playerSport(p: ReportParams) {
  const id = String(p.player_id ?? '')
  if (!rosterById[id]) return []
  const f = playerFinance(id)
  return PLAYER_SPORTS.map((sport, i) => {
    const share = [0.5, 0.25, 0.15, 0.1][i] ?? 0.1
    const bet = Math.round(f.sportBet * share)
    const ggr = Math.round(f.sportGGR * share)
    return { sport, bet_amount: bet, win_amount: bet - ggr, ggr, bet_count: Math.round(bet / 25) }
  })
}

function playerActivity(p: ReportParams) {
  const id = String(p.player_id ?? '')
  if (!rosterById[id]) return []
  const f = playerFinance(id)
  const dates = range(p)
  const n = Math.max(dates.length, 1)
  return dates.map((date) => {
    const k = 0.6 + seeded(`pa|${id}|${date}`) * 0.8
    return {
      date,
      casino_bet: Math.round((f.casinoBet / n) * k),
      casino_win: Math.round(((f.casinoBet - f.casinoGGR) / n) * k),
      casino_ggr: Math.round((f.casinoGGR / n) * k),
      casino_rounds: Math.round((f.rounds / n) * k),
      sport_bet: Math.round((f.sportBet / n) * k),
      sport_win: Math.round(((f.sportBet - f.sportGGR) / n) * k),
      sport_ggr: Math.round((f.sportGGR / n) * k),
      deposits: Math.round((f.deposits / n) * k),
      withdrawals: Math.round((f.withdrawals / n) * k),
    }
  })
}

function playerTransactions(p: ReportParams) {
  const id = String(p.player_id ?? '')
  const pl = rosterById[id]
  if (!pl) return []
  const f = playerFinance(id)
  const rows: Record<string, unknown>[] = []
  const count = Math.min(20, f.depositCount + f.withdrawalCount)
  for (let i = 0; i < count; i++) {
    const isDep = seeded(`ptx|${id}|${i}`) > 0.4
    const amt = Math.round((isDep ? f.deposits / f.depositCount : f.withdrawals / Math.max(1, f.withdrawalCount)) * (0.6 + seeded(`pta|${id}|${i}`) * 0.8))
    rows.push({
      event_date: recentDate(i),
      event_type: isDep ? 'deposit' : 'withdrawal',
      player_id: id,
      site_id: pl.site_id,
      payment_method: PAYMENT_METHODS[i % PAYMENT_METHODS.length],
      payment_method_type: PM_TYPE[PAYMENT_METHODS[i % PAYMENT_METHODS.length]] ?? 'instant',
      amount: amt,
      amount_eur: pl.currency === 'BRL' ? Math.round(amt * 0.182) : amt,
      currency: pl.currency,
      movement_id: 500000 + Number(id) * 100 + i,
      external_transaction_id: `TX-${id}-${i}`,
      is_first_deposit: isDep && i === count - 1,
    })
  }
  return rows
}

// ---- currencies ------------------------------------------------------------

interface DemoCurrencyRate {
  code: string
  rateEur: number // value of 1 unit in EUR
  rateUsd: number // value of 1 unit in USD
}

const DEMO_CCY_RATES: DemoCurrencyRate[] = [
  { code: 'EUR', rateEur: 1, rateUsd: 1.08 },
  { code: 'USD', rateEur: 0.926, rateUsd: 1 },
  { code: 'BRL', rateEur: 0.182, rateUsd: 0.197 },
  { code: 'GBP', rateEur: 1.172, rateUsd: 1.266 },
  { code: 'MXN', rateEur: 0.054, rateUsd: 0.058 },
  { code: 'CLP', rateEur: 0.00098, rateUsd: 0.00106 },
  { code: 'PEN', rateEur: 0.247, rateUsd: 0.267 },
  { code: 'NGN', rateEur: 0.00061, rateUsd: 0.00066 },
]

function currencyRows() {
  const last = isoDay(new Date())
  return DEMO_CCY_RATES.map((c) => ({
    currency: c.code,
    rate_eur: c.rateEur,
    rate_usd: c.rateUsd,
    last_date: last,
  }))
}

function currencyRateRows(p: ReportParams) {
  const rows: Record<string, unknown>[] = []
  const want = String(p.currency ?? '').toUpperCase()
  const codes = DEMO_CCY_RATES.filter((c) => c.code !== 'EUR' && (!want || c.code === want))
  for (const date of range(p)) {
    for (const c of codes) {
      const jitter = 1 + (seeded(`fx|${date}|${c.code}`) - 0.5) * 0.04 // ±2%
      rows.push({
        date,
        currency_from: c.code,
        currency_to: 'EUR',
        rate: Number((c.rateEur * jitter).toFixed(6)),
        source: 'ECB',
        is_fallback: false,
      })
    }
  }
  return rows
}

// ---- ad-hoc query ----------------------------------------------------------

function queryResult(p: ReportParams) {
  const rows = ggrRows({ ...p, display_currency: 'EUR' }).slice(0, 20).map((r) => ({
    event_date: r.date,
    ggr: r.ggr,
    ngr: r.ngr,
    turnover: r.turnover,
  }))
  return {
    columns: ['event_date', 'ggr', 'ngr', 'turnover'],
    rows,
    row_count: rows.length,
    execution_time_ms: 12,
  }
}

// ---- resolver --------------------------------------------------------------

// demoResolve maps a request URL + params to the response BODY (the envelope
// the Go API would return: { data: [...] }). Returns undefined for unknown
// paths so the caller can decide how to handle them.
export function demoResolve(url: string, params: ReportParams = {}): unknown | undefined {
  const u = url.split('?')[0]
  const wrap = (rows: unknown) => ({ data: rows })

  if (u.endsWith('/auth/me')) return DEMO_USER
  if (u.endsWith('/me/tenants')) return wrap(DEMO_TENANTS)
  if (u.endsWith('/auth/login')) {
    return { access_token: 'demo.access.token', refresh_token: 'demo.refresh.token' }
  }

  if (u.endsWith('/reports/ggr')) return wrap(ggrRows(params))
  if (u.endsWith('/reports/cashier/deposits')) return wrap(depositRows(params))
  if (u.endsWith('/reports/cashier/withdrawals')) return wrap(withdrawalRows(params))
  if (u.endsWith('/reports/cashier/overview')) return wrap(cashierOverview(params))
  if (u.endsWith('/reports/cashier/mix')) return wrap(mixRows(params))
  if (u.endsWith('/reports/cashier/ftd')) return wrap(ftdRows(params))
  if (u.endsWith('/reports/players/active')) return wrap(activePlayerRows(params))
  if (u.endsWith('/reports/bonus')) return wrap(bonusRows(params))
  if (u.endsWith('/reports/sportsbook/overview')) return wrap(sportsbookRows(params))
  if (u.endsWith('/reports/sportsbook/by-sport')) return wrap(sportsbookBySport(params))
  if (u.endsWith('/reports/cohorts/retention')) return wrap(cohortRows(params))

  // Player leaderboard source + single-player detail report
  if (u.endsWith('/reports/players/leaderboard')) return wrap(playerLeaderboardRows(params))
  if (u.endsWith('/reports/cashier/players')) return wrap(playerCashierRows(params))
  if (u.endsWith('/reports/cashier/transactions')) return wrap(playerTransactions(params))
  if (u.endsWith('/reports/players/detail/profile')) return wrap(playerProfile(params))
  if (u.endsWith('/reports/players/detail/summary')) return wrap(playerSummary(params))
  if (u.endsWith('/reports/players/detail/casino')) return wrap(playerCasinoGames(params))
  if (u.endsWith('/reports/players/detail/sport')) return wrap(playerSport(params))
  if (u.endsWith('/reports/players/detail/activity')) return wrap(playerActivity(params))

  if (u.endsWith('/reports/games/overview')) return wrap(gameOverview(params))
  if (u.endsWith('/reports/games/performance')) return wrap(gamePerformanceRows(params))
  if (u.endsWith('/reports/games/mix')) return wrap(gameMixRows(params))
  if (u.endsWith('/reports/games/top')) return wrap(gamePerformanceRows(params))
  if (u.endsWith('/reports/games/players')) return wrap(gamePlayersRows(params))
  if (u.endsWith('/reports/brands/margin')) return wrap(brandMarginRows(params))

  // Currencies page
  if (u.endsWith('/currencies')) return wrap(currencyRows())
  if (u.endsWith('/currencies/rates')) return wrap(currencyRateRows(params))

  // Ad-hoc Query Builder
  if (u.endsWith('/query')) return wrap(queryResult(params))

  // Alerts (empty list → page shows its empty state); mutations return ok
  if (u.endsWith('/alerts') || u.endsWith('/alerts/history')) return wrap([])
  if (u.includes('/alerts/rule')) return wrap({ id: 1 })

  // Connectors / data sources (empty list)
  if (u.includes('/connectors/')) return wrap([])

  // Cashier players / transactions and player-detail drill-downs: empty but
  // valid so pages render their empty states instead of erroring.
  if (u.includes('/reports/')) return wrap([])

  // Safe default: an empty list envelope never breaks a page's `.data || []`.
  return wrap([])
}
